import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { categories, getComicsByCategory } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeSort, setActiveSort] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const currentCategory = useMemo(() => categories.find(c => c.slug === slug) || categories[0], [slug]);

  const comics = useMemo(() => {
    const list = getComicsByCategory(slug);
    if (activeSort === 'latest') return [...list].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    if (activeSort === 'popular') return [...list].sort((a, b) => b.views - a.views);
    return [...list].sort((a, b) => b.rating - a.rating);
  }, [slug, activeSort]);

  const collectionPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 韩漫漫画网站`,
    description: `韩漫漫画网站${currentCategory.name}分类，提供海量免费正版韩国漫画在线阅读。${currentCategory.description}`,
    url: `https://www.manhwa-reader.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫漫画网站',
      url: 'https://www.manhwa-reader.com',
    },
  };

  const sortOptions: { key: 'latest' | 'popular' | 'rating'; label: string; icon: string }[] = [
    { key: 'latest', label: '最新', icon: 'ri-time-line' },
    { key: 'popular', label: '最热', icon: 'ri-fire-line' },
    { key: 'rating', label: '评分', icon: 'ri-star-line' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(collectionPageSchema)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '分类浏览', href: '/category/all' },
              { label: currentCategory.name },
            ]} />

            <div className="mt-6 mb-8">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-800 mb-2">{currentCategory.name}</h1>
              <p className="text-sm text-foreground-500">{currentCategory.description}</p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map(cat => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${slug === cat.slug ? 'bg-primary-500 text-white font-medium' : 'bg-background-50 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 border border-background-200'}`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center justify-between mb-8">
              <p className="text-sm text-foreground-500">共找到 <strong className="text-foreground-800">{comics.length}</strong> 部漫画</p>
              <div className="flex items-center gap-1 bg-background-50 rounded-full p-1 border border-background-200">
                {sortOptions.map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setActiveSort(opt.key)}
                    className={`flex items-center gap-1 px-4 py-1.5 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${activeSort === opt.key ? 'bg-secondary-500 text-white font-medium' : 'text-foreground-600 hover:text-secondary-600'}`}
                  >
                    <i className={opt.icon}></i>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {mounted && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                {comics.map(comic => (
                  <ComicCard key={comic.id} {...comic} />
                ))}
              </div>
            )}

            {comics.length === 0 && (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">该分类下暂无漫画</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}